import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiError } from "@/api/errors";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

import { type FormField, summarizeFormSchema } from "./summarize-form-schema";
import { useProcessModelFile } from "./use-process-model";
import { useSaveModelFile } from "./use-save-model-file";
import {
  FORM_SCHEMA_ISSUE_KEYS,
  validateFormSchema,
} from "./validate-form-schema";

export type FormFileEditorProps = {
  readonly processModelId: string;
  readonly fileName: string;
};

/**
 * ヒューマンタスクのフォーム定義（JSON Schema / UI Schema）を確認・編集する。
 *
 * 編集は生 JSON で行う（項目を組み立てる GUI は作らない。RJSF が受け取る範囲は
 * 広く、GUI で表せる形に狭めると既存の定義を扱えなくなる）。
 * 保存は図と同じ経路（multipart PUT ＋ ハッシュによる競合検知）。
 *
 * 以前は独立した「フォーム設計」画面だったが、同じモデルを 2 画面で選び直す必要が
 * あったのでプロセス設計のモデル詳細に統合した。
 *
 * 下書きは「編集を始めたときのハッシュ」と一緒に持つ。同じ画面のフォーム定義の取り込みが
 * このファイルを上書きして読み直すと、クエリのハッシュは新しくなる。保存時にそれを
 * 使うと 409 にならず、古い下書きで取り込んだ内容を黙って巻き戻してしまう。
 */
export function FormFileEditor({
  processModelId,
  fileName,
}: FormFileEditorProps) {
  const { t } = useTranslation("common");
  const { data, isPending, isError } = useProcessModelFile(
    processModelId,
    fileName,
  );
  const save = useSaveModelFile();
  const [draft, setDraft] = useState<{
    readonly text: string;
    /** 編集を始めた時点の内容のハッシュ。保存はこれを添える。 */
    readonly baseHash: string | null;
  } | null>(null);

  if (isPending) return <Skeleton className="h-48 w-full" />;
  if (isError || !data) {
    return <p className="text-destructive text-sm">{t("state.error")}</p>;
  }

  const isEditing = draft !== null;
  const text = draft === null ? data.contents : draft.text;
  const validation = validateFormSchema(text);
  // 編集中は入力から要約する（保存前に内容を確かめられる）
  const fields = summarizeFormSchema(
    validation.ok ? validation.value : parseJson(text),
  );

  return (
    <div className="border-border flex min-w-0 flex-col gap-3 rounded-md border p-3">
      <div className="flex items-center justify-between gap-2">
        <h4 className="font-mono text-sm font-semibold">{fileName}</h4>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() =>
            setDraft(
              isEditing
                ? null
                : { text: data.contents, baseHash: data.contentsHash },
            )
          }
        >
          {t(isEditing ? "formDesign.cancelEdit" : "formDesign.edit")}
        </Button>
      </div>

      {isEditing ? (
        <p className="bg-muted text-muted-foreground rounded-md px-3 py-2 text-xs">
          {t("formDesign.editHint")}
        </p>
      ) : null}

      {fields.length === 0 && !isEditing ? (
        <p className="text-muted-foreground text-sm">
          {t("formDesign.notForm")}
        </p>
      ) : fields.length === 0 ? null : (
        <section
          aria-label={t("formDesign.fields")}
          className="flex flex-col gap-2"
        >
          <h5 className="text-sm font-semibold">{t("formDesign.fields")}</h5>
          <ul className="flex flex-col gap-2 text-sm">
            {fields.map((field) => (
              <FieldRow key={field.name} field={field} />
            ))}
          </ul>
        </section>
      )}

      <section className="flex min-w-0 flex-col gap-2">
        <h5 className="text-sm font-semibold">{t("formDesign.raw")}</h5>
        {isEditing ? (
          <>
            <label className="flex flex-col gap-1 text-xs">
              <span className="sr-only">{t("formDesign.editorLabel")}</span>
              <textarea
                aria-label={t("formDesign.editorLabel")}
                value={text}
                onChange={(event) =>
                  setDraft((current) =>
                    current === null
                      ? null
                      : { ...current, text: event.target.value },
                  )
                }
                rows={16}
                spellCheck={false}
                className="border-border bg-background focus-visible:ring-ring w-full rounded-md border p-3 font-mono text-xs focus-visible:ring-2 focus-visible:outline-none"
              />
            </label>

            {validation.ok ? null : (
              <p role="alert" className="text-destructive text-sm">
                {t(FORM_SCHEMA_ISSUE_KEYS[validation.reason], {
                  detail: validation.detail ?? "",
                })}
              </p>
            )}

            <SaveRow
              mutation={save}
              canSave={validation.ok && !save.isPending}
              onSave={() =>
                save.mutate({
                  processModelId,
                  fileName,
                  contents: text,
                  contentsHash:
                    draft === null ? data.contentsHash : draft.baseHash,
                })
              }
            />
          </>
        ) : (
          <pre className="bg-muted max-h-72 overflow-auto rounded-md p-3 text-xs">
            {data.contents}
          </pre>
        )}
      </section>
    </div>
  );
}

function SaveRow({
  mutation,
  canSave,
  onSave,
}: {
  readonly mutation: ReturnType<typeof useSaveModelFile>;
  readonly canSave: boolean;
  readonly onSave: () => void;
}) {
  const { t } = useTranslation("common");
  const isConflict =
    mutation.error instanceof ApiError && mutation.error.status === 409;

  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-sm">
        {mutation.isSuccess ? (
          <span className="font-medium">{t("processDesign.saved")}</span>
        ) : null}
        {mutation.isError ? (
          <span role="alert" className="text-destructive">
            {t(
              isConflict
                ? "processDesign.saveConflict"
                : "processDesign.saveFailed",
            )}
          </span>
        ) : null}
      </span>
      <Button type="button" disabled={!canSave} onClick={onSave}>
        {t("caseDetail.saveDiagram")}
      </Button>
    </div>
  );
}

function FieldRow({ field }: { readonly field: FormField }) {
  const { t } = useTranslation("common");

  return (
    <li className="border-border flex flex-wrap items-baseline gap-2 border-b pb-2 last:border-0">
      <span className="font-medium">{field.label}</span>
      <span className="text-muted-foreground font-mono text-xs">
        {field.name}
      </span>
      <Badge variant="outline">{field.type}</Badge>
      {field.required ? (
        <Badge variant="secondary">{t("formDesign.required")}</Badge>
      ) : null}
      {field.choices === null ? null : (
        <span className="text-muted-foreground text-xs">
          {t("formDesign.choices")}: {field.choices.join(" / ")}
        </span>
      )}
    </li>
  );
}

/** 壊れた JSON でも画面を落とさない（設計中のファイルが不正なこともある）。 */
function parseJson(contents: string): unknown {
  try {
    return JSON.parse(contents);
  } catch {
    return null;
  }
}
